import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, CheckCircle2Icon, CheckIcon, ChevronUpIcon, CircleAlertIcon, NotebookTextIcon, PlayIcon, ScanLineIcon } from 'lucide-react';
import { OrderWorkflow } from './OrderWorkflow';
type RunningOrdersProps = {
  onBack: () => void;
  isInitialLoading?: boolean;
};
type RunningOrderMaterial = {
  articleNumber: string;
  description: string;
  quantityPerLotKg: number;
  isWeighed?: boolean;
};
type RunningOrder = {
  id: string;
  product: string;
  lots: number;
  completedLots: number;
  materials: RunningOrderMaterial[];
};
const runningOrders: RunningOrder[] = [{
  id: 'AUF-240718-031',
  product: 'Backdekoration',
  lots: 12,
  completedLots: 4,
  materials: [{
    articleNumber: 'BD-1042',
    description: 'Zuckerstreusel, bunt',
    quantityPerLotKg: 120,
    isWeighed: true
  }, {
    articleNumber: 'BD-1178',
    description: 'Schokoraspel, fein',
    quantityPerLotKg: 80,
    isWeighed: true
  }, {
    articleNumber: 'BD-1231',
    description: 'Dekorzucker, weiß',
    quantityPerLotKg: 50
  }]
}, {
  id: 'AUF-240718-032',
  product: 'Binde- & Geliermittel',
  lots: 8,
  completedLots: 3,
  materials: [{
    articleNumber: 'BG-2014',
    description: 'Pektin, Apfel',
    quantityPerLotKg: 300,
    isWeighed: true
  }, {
    articleNumber: 'BG-2039',
    description: 'Agar-Agar, Pulver',
    quantityPerLotKg: 125
  }, {
    articleNumber: 'BG-2097',
    description: 'Johannisbrotkernmehl',
    quantityPerLotKg: 75
  }]
}, {
  id: 'AUF-240718-033',
  product: 'Bioland Honige',
  lots: 16,
  completedLots: 7,
  materials: [{
    articleNumber: 'BH-3008',
    description: 'Blütenhonig, Bioland',
    quantityPerLotKg: 150,
    isWeighed: true
  }, {
    articleNumber: 'BH-3041',
    description: 'Waldhonig, Bioland',
    quantityPerLotKg: 100
  }]
}, {
  id: 'AUF-240718-034',
  product: 'Öle & Fette',
  lots: 10,
  completedLots: 5,
  materials: [{
    articleNumber: 'OF-4022',
    description: 'Sonnenblumenöl, kaltgepresst',
    quantityPerLotKg: 350,
    isWeighed: true
  }, {
    articleNumber: 'OF-4056',
    description: 'Kakaobutter, Bio',
    quantityPerLotKg: 150
  }]
}, {
  id: 'AUF-240718-035',
  product: 'Brotaufstriche',
  lots: 20,
  completedLots: 9,
  materials: [{
    articleNumber: 'BA-5013',
    description: 'Haselnussmus, geröstet',
    quantityPerLotKg: 140,
    isWeighed: true
  }, {
    articleNumber: 'BA-5064',
    description: 'Kakaopulver, stark entölt',
    quantityPerLotKg: 60
  }, {
    articleNumber: 'BA-5098',
    description: 'Rohrzucker, fein',
    quantityPerLotKg: 50
  }]
}, {
  id: 'AUF-240718-036',
  product: 'Fruchtmischung Mustercharge',
  lots: 1,
  completedLots: 0,
  materials: [{
    articleNumber: 'FM-6012',
    description: 'Apfelstücke, getrocknet',
    quantityPerLotKg: 45,
    isWeighed: true
  }, {
    articleNumber: 'FM-6048',
    description: 'Cranberries, gesüßt',
    quantityPerLotKg: 30,
    isWeighed: true
  }, {
    articleNumber: 'FM-6073',
    description: 'Kürbiskerne, geröstet',
    quantityPerLotKg: 18
  }, {
    articleNumber: 'FM-6095',
    description: 'Zimt, gemahlen',
    quantityPerLotKg: 2.5
  }]
}];
export function RunningOrders({
  onBack,
  isInitialLoading = false
}: RunningOrdersProps) {
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [startedOrderId, setStartedOrderId] = useState<string | null>(null);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [isScannerDialogOpen, setIsScannerDialogOpen] = useState(false);
  const [isScannerScanning, setIsScannerScanning] = useState(false);
  const [scanAttempt, setScanAttempt] = useState(0);
  const [isListLoading, setIsListLoading] = useState(isInitialLoading);
  const scannedOrder = runningOrders[0];
  const isScanResultFound = scanAttempt % 2 === 1;
  const selectedOrder = runningOrders.find(order => order.id === (startedOrderId ?? selectedOrderId));
  useEffect(() => {
    if (!isScannerDialogOpen || !isScannerScanning) {
      return;
    }
    const scanTimeoutId = window.setTimeout(() => {
      setIsScannerScanning(false);
    }, 3000);
    return () => window.clearTimeout(scanTimeoutId);
  }, [isScannerDialogOpen, isScannerScanning]);
  useEffect(() => {
    if (!isListLoading) {
      return;
    }
    const listTimeoutId = window.setTimeout(() => {
      setIsListLoading(false);
    }, 2000);
    return () => window.clearTimeout(listTimeoutId);
  }, [isListLoading]);
  function openScannerDialog() {
    setScanAttempt(currentAttempt => currentAttempt + 1);
    setIsScannerScanning(true);
    setIsScannerDialogOpen(true);
  }
  function closeScannerDialog() {
    setIsScannerDialogOpen(false);
    setIsScannerScanning(false);
  }
  function selectOrder(order: RunningOrder) {
    setSelectedOrderId(order.id);
  }
  function startOrder(order: RunningOrder) {
    setSelectedOrderId(order.id);
    setStartedOrderId(order.id);
  }
  function startScannedOrder() {
    startOrder(scannedOrder);
    closeScannerDialog();
  }
  function toggleMaterials(orderId: string) {
    setExpandedOrderId(currentOrderId => currentOrderId === orderId ? null : orderId);
  }
  return <main className="min-h-screen w-full bg-surface-subtle font-body text-text-primary" data-id="element-950">
      {startedOrderId && selectedOrder ? <OrderWorkflow order={selectedOrder} initialLot={Math.min(selectedOrder.completedLots + 1, selectedOrder.lots)} initialWeighedMaterialIds={selectedOrder.materials.filter(material => material.isWeighed).map(material => material.articleNumber)} onCancel={() => setStartedOrderId(null)} data-id="element-951" /> : <section className="mx-auto w-full max-w-[1540px] px-5 py-6 sm:px-8 sm:py-8 md:px-10 lg:px-14 xl:px-20" data-id="element-952">
          <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} data-id="element-953">
            <div className="flex flex-col gap-5 border-b border-border pb-6 lg:flex-row lg:items-center lg:justify-between" data-id="element-954">
              <div className="flex min-w-0 items-center gap-4 sm:gap-5" data-id="element-955">
                <button type="button" onClick={onBack} className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md border border-border bg-surface text-text-primary transition-colors hover:bg-brand-yellow-light focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green active:bg-brand-green" aria-label="Zurück zum Produktionsmenü" data-id="element-956">
                  <ArrowLeftIcon size={26} strokeWidth={1.9} aria-hidden="true" data-id="element-957" />
                </button>
                <div data-id="element-958">
                  <h1 className="font-heading text-3xl leading-tight sm:text-4xl" data-id="element-959">
                    Aufträge
                  </h1>
                </div>
              </div>

              <button type="button" onClick={openScannerDialog} aria-haspopup="dialog" aria-expanded={isScannerDialogOpen} className="flex min-h-[72px] items-center gap-4 rounded-lg border border-border bg-surface px-4 py-3 text-left shadow-sm transition-colors hover:border-brand-green hover:bg-brand-yellow-light focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green active:bg-brand-green sm:px-5 lg:w-[360px]" data-id="element-960">
                <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-brand-yellow-light" data-id="element-961">
                  <ScanLineIcon className="text-text-primary" size={24} strokeWidth={1.7} aria-hidden="true" data-id="element-962" />
                  <motion.span className="absolute -right-1 -top-1 h-3 w-3 rounded-full border-2 border-surface bg-brand-green" animate={{
                scale: [1, 1.25, 1],
                opacity: [1, 0.65, 1]
              }} transition={{
                duration: 2,
                ease: 'easeInOut',
                repeat: Infinity
              }} aria-hidden="true" data-id="element-963" />
                </div>
                <span className="min-w-0" data-id="element-964">
                  <span className="block font-heading text-base text-text-primary" data-id="element-965">
                    Mit Handscanner fortsetzen
                  </span>
                  <span className="mt-0.5 block text-sm leading-5 text-text-secondary" data-id="element-966">
                    Auftrag scannen und direkt fortsetzen.
                  </span>
                </span>
              </button>
            </div>

            <ul className="mt-5 grid grid-cols-1 gap-4 pb-8" aria-busy={isListLoading} aria-label={isListLoading ? 'Aufträge werden geladen' : 'Aufträge'} data-id="element-967">
              {isListLoading ? <RunningOrdersSkeleton data-id="element-968" /> : runningOrders.map((order, index) => {
            const isSelected = selectedOrderId === order.id;
            const isStarted = startedOrderId === order.id;
            const isExpanded = expandedOrderId === order.id;
            const weightPerLotKg = getWeightPerLot(order);
            const totalWeightKg = weightPerLotKg * order.lots;
            const completedLotPercentage = order.completedLots / order.lots * 100;
            const detailId = `${order.id}-materials`;
            return <motion.li key={order.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.25,
              delay: index * 0.05,
              ease: 'easeOut'
            }} className={`rounded-lg border bg-surface p-3 transition-shadow sm:p-4 ${isSelected ? 'border-brand-green shadow-md' : 'border-border shadow-sm hover:border-text-secondary'}`} data-id="element-969">
                      <article className="flex min-h-[124px] items-stretch gap-2 sm:gap-4" data-id="element-970">
                        <button type="button" onClick={() => selectOrder(order)} aria-pressed={isSelected} className="flex min-w-0 flex-1 items-center gap-3 rounded-md px-2 text-left transition-colors hover:bg-surface-subtle focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green sm:gap-4 sm:px-3" data-id="element-971">
                          <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-md font-heading text-lg ${isSelected ? 'bg-brand-green' : 'border border-border bg-surface-subtle'}`} aria-hidden="true" data-id="element-972">
                            {index + 1}
                          </span>

                          <span className="min-w-0 flex-1" data-id="element-973">
                            <span className="font-heading text-base sm:text-lg" data-id="element-974">
                              {order.id}
                            </span>
                            <span className="mt-1 block truncate text-sm text-text-secondary sm:text-base" data-id="element-975">
                              {order.product}
                            </span>
                            <span className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-secondary" data-id="element-976">
                              <span data-id="element-977">
                                {order.lots} × {formatWeight(weightPerLotKg)} kg
                              </span>
                              <span aria-hidden="true" data-id="element-978">·</span>
                              <span data-id="element-979">
                                Gesamt:{' '}
                                <span className="font-heading text-text-primary" data-id="element-980">
                                  {formatWeight(totalWeightKg)} kg
                                </span>
                              </span>
                            </span>
                            <span className="mt-3 flex items-center gap-2.5" data-id="element-981">
                              <span className="h-2 w-20 overflow-hidden rounded-full bg-border sm:w-32" aria-hidden="true" data-id="element-982">
                                <span className="block h-full rounded-full bg-brand-green transition-all duration-500" style={{
                          width: `${completedLotPercentage}%`
                        }} data-id="element-983" />
                              </span>
                              <span className="font-heading text-sm" data-id="element-984">
                                {order.completedLots.toLocaleString('de-DE')} /{' '}
                                {order.lots.toLocaleString('de-DE')}
                              </span>
                              <span className="hidden text-sm text-text-secondary sm:inline" data-id="element-985">
                                Lots verwogen
                              </span>
                            </span>
                          </span>
                        </button>

                        <button type="button" onClick={() => toggleMaterials(order.id)} aria-expanded={isExpanded} aria-controls={detailId} aria-label={isExpanded ? `Rezeptur für ${order.product} schließen` : `Rezeptur für ${order.product} öffnen`} className={`flex min-h-[56px] w-14 shrink-0 items-center justify-center self-center rounded-md border transition-colors focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green active:scale-95 sm:h-16 sm:w-16 ${isExpanded ? 'border-brand-green bg-brand-yellow-light text-text-primary' : 'border-border bg-surface text-text-secondary hover:bg-surface-subtle'}`} data-id="element-986">
                          {isExpanded ? <ChevronUpIcon size={26} strokeWidth={2} aria-hidden="true" data-id="element-987" /> : <NotebookTextIcon size={24} strokeWidth={1.9} aria-hidden="true" data-id="element-988" />}
                        </button>

                        <button type="button" onClick={() => startOrder(order)} className={`flex min-h-[56px] w-14 shrink-0 items-center justify-center self-center rounded-md transition-colors focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green active:scale-95 sm:h-16 sm:w-16 ${isStarted ? 'bg-success text-surface' : 'bg-brand-green text-text-primary hover:bg-brand-green-dark'}`} aria-label={isStarted ? `${order.id} ausgewählt` : `${order.id} zur Abfüllung auswählen`} data-id="element-989">
                          {isStarted ? <CheckCircle2Icon size={25} strokeWidth={2} aria-hidden="true" data-id="element-990" /> : <PlayIcon className="ml-0.5" size={25} fill="currentColor" strokeWidth={1.8} aria-hidden="true" data-id="element-991" />}
                        </button>
                      </article>

                      <AnimatePresence initial={false} data-id="element-992">
                        {isExpanded && <motion.section id={detailId} aria-label={`Rezeptur für ${order.product}`} initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.24,
                  ease: 'easeOut'
                }} className="overflow-hidden" data-id="element-993">
                            <div className="mt-4 border-t border-border pt-4" data-id="element-994">
                              <div className="overflow-x-auto" data-id="element-995">
                                <table className="w-full min-w-[540px] text-left text-sm" data-id="element-996">
                                  <thead className="text-xs uppercase tracking-wide text-text-secondary" data-id="element-997">
                                    <tr data-id="element-998">
                                      <th scope="col" className="pb-2 pr-4 font-normal" data-id="element-999">
                                        Artikelnummer
                                      </th>
                                      <th scope="col" className="pb-2 pr-4 font-normal" data-id="element-1000">
                                        Beschreibung
                                      </th>
                                      <th scope="col" className="whitespace-nowrap pb-2 pr-4 text-center font-normal" data-id="element-1001">
                                        Verwogen
                                      </th>
                                      <th scope="col" className="whitespace-nowrap pb-2 pr-4 text-right font-normal" data-id="element-1002">
                                        Pro Lot
                                      </th>
                                      <th scope="col" className="whitespace-nowrap pb-2 text-right font-normal" data-id="element-1003">
                                        Pro Charge
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-1004">
                                    {order.materials.map(material => {
                            const materialTotalWeightKg = material.quantityPerLotKg * order.lots;
                            return <tr key={material.articleNumber} className="border-t border-border" data-id="element-1005">
                                          <td className="whitespace-nowrap py-3 pr-4 font-heading" data-id="element-1006">
                                            {material.articleNumber}
                                          </td>
                                          <td className="py-3 pr-4 text-text-secondary" data-id="element-1007">
                                            {material.description}
                                          </td>
                                          <td className="py-3 pr-4 text-center" data-id="element-1008">
                                            {material.isWeighed ? <span className="inline-flex items-center justify-center text-success" aria-label="Verwogen" data-id="element-1009">
                                                <CheckIcon size={20} strokeWidth={2.6} aria-hidden="true" data-id="element-1010" />
                                                <span className="sr-only" data-id="element-1011">
                                                  Verwogen
                                                </span>
                                              </span> : <span className="font-heading text-base text-text-secondary" aria-label="Noch nicht verwogen" data-id="element-1012">
                                                <span aria-hidden="true" data-id="element-1013">
                                                  −
                                                </span>
                                                <span className="sr-only" data-id="element-1014">
                                                  Noch nicht verwogen
                                                </span>
                                              </span>}
                                          </td>
                                          <td className="whitespace-nowrap py-3 pr-4 text-right font-heading" data-id="element-1015">
                                            {formatWeight(material.quantityPerLotKg)}{' '}
                                            kg
                                          </td>
                                          <td className="whitespace-nowrap py-3 text-right font-heading" data-id="element-1016">
                                            {formatWeight(materialTotalWeightKg)}{' '}
                                            kg
                                          </td>
                                        </tr>;
                          })}
                                  </tbody>
                                  <tfoot data-id="element-1017">
                                    <tr className="border-t-2 border-text-primary" data-id="element-1018">
                                      <th scope="row" colSpan={3} className="pt-3 text-left font-heading" data-id="element-1019">
                                        Gesamtgewicht · {order.lots} Lots
                                      </th>
                                      <td className="whitespace-nowrap pt-3 pr-4 text-right font-heading" data-id="element-1020">
                                        {formatWeight(weightPerLotKg)} kg
                                      </td>
                                      <td className="whitespace-nowrap pt-3 text-right font-heading" data-id="element-1021">
                                        {formatWeight(totalWeightKg)} kg
                                      </td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            </div>
                          </motion.section>}
                      </AnimatePresence>
                    </motion.li>;
          })}
            </ul>

            <AnimatePresence data-id="element-1022">
              {isScannerDialogOpen && <motion.div className="fixed inset-0 z-20 flex items-end justify-center bg-text-primary/50 p-4 backdrop-blur-md sm:items-center sm:p-8 lg:p-10" role="presentation" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onMouseDown={closeScannerDialog} data-id="element-1023">
                  <motion.section role="dialog" aria-modal="true" aria-labelledby="running-scanner-dialog-title" aria-describedby="running-scanner-dialog-description" className="w-full max-w-5xl overflow-hidden rounded-2xl border border-border bg-surface shadow-md" initial={{
              opacity: 0,
              y: 24,
              scale: 0.98
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: 20,
              scale: 0.98
            }} transition={{
              duration: 0.2,
              ease: 'easeOut'
            }} onMouseDown={event => event.stopPropagation()} data-id="element-1024">
                    {isScannerScanning ? <div className="animate-pulse p-6 sm:p-8" role="status" aria-live="polite" aria-label="Handscanner sucht nach einem Auftrag" data-id="element-1025">
                        <div className="flex items-center gap-5 border-b border-border pb-6 sm:pb-8" data-id="element-1026">
                          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-brand-yellow-light" data-id="element-1027">
                            <ScanLineIcon size={31} strokeWidth={1.7} aria-hidden="true" data-id="element-1028" />
                          </div>
                          <div className="min-w-0 flex-1" data-id="element-1029">
                            <p className="text-base text-text-secondary" data-id="element-1030">
                              Handscanner · Auftrag wird gesucht
                            </p>
                            <div className="mt-3 h-9 w-52 rounded-md bg-border sm:w-72" data-id="element-1031" />
                            <div className="mt-3 h-5 w-36 rounded-md bg-border sm:w-52" data-id="element-1032" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 pt-6 sm:grid-cols-3 sm:pt-8" data-id="element-1033">
                          {[0, 1, 2].map(item => <div key={item} className={`rounded-lg border border-border bg-surface-subtle p-4 ${item === 2 ? 'col-span-2 sm:col-span-1' : ''}`} data-id="element-1034">
                              <div className="h-4 w-20 rounded bg-border" data-id="element-1035" />
                              <div className="mt-3 h-7 w-16 rounded bg-border" data-id="element-1036" />
                            </div>)}
                        </div>
                        <div className="mt-8" data-id="element-1037">
                          <div className="h-7 w-32 rounded-md bg-border" data-id="element-1038" />
                          <div className="mt-4 overflow-hidden rounded-lg border border-border" data-id="element-1039">
                            <div className="h-12 bg-surface-subtle" data-id="element-1040" />
                            <div className="space-y-3 p-4" data-id="element-1041">
                              {[0, 1, 2].map(item => <div key={item} className="h-6 w-full rounded bg-border" data-id="element-1042" />)}
                            </div>
                          </div>
                        </div>
                        <div className="mt-8 border-t border-border pt-5 text-center text-base text-text-secondary" data-id="element-1043">
                          Auftrag wird geladen...
                        </div>
                      </div> : isScanResultFound ? <>
                        <div className="flex items-start gap-5 border-b border-border bg-surface-subtle p-6 sm:p-8" data-id="element-1044">
                          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-brand-yellow-light" data-id="element-1045">
                            <ScanLineIcon size={31} strokeWidth={1.7} aria-hidden="true" data-id="element-1046" />
                          </div>
                          <div className="min-w-0" data-id="element-1047">
                            <h2 id="running-scanner-dialog-title" className="font-heading text-3xl leading-tight sm:text-4xl" data-id="element-1048">
                              {scannedOrder.id}
                            </h2>
                            <p id="running-scanner-dialog-description" className="mt-2 text-lg text-text-secondary" data-id="element-1049">
                              {scannedOrder.product}
                            </p>
                          </div>
                        </div>

                        <div className="max-h-[56vh] overflow-y-auto p-6 sm:p-8" data-id="element-1050">
                          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3" data-id="element-1051">
                            <div className="rounded-lg border border-border bg-surface-subtle p-4" data-id="element-1052">
                              <dt className="text-sm uppercase tracking-wide text-text-secondary" data-id="element-1053">
                                Lots
                              </dt>
                              <dd className="mt-1.5 font-heading text-2xl" data-id="element-1054">
                                {scannedOrder.lots}
                              </dd>
                            </div>
                            <div className="rounded-lg border border-border bg-surface-subtle p-4" data-id="element-1055">
                              <dt className="text-sm uppercase tracking-wide text-text-secondary" data-id="element-1056">
                                Gewicht pro Lot
                              </dt>
                              <dd className="mt-1.5 font-heading text-2xl" data-id="element-1057">
                                {formatWeight(getWeightPerLot(scannedOrder))} kg
                              </dd>
                            </div>
                            <div className="col-span-2 rounded-lg border border-border bg-surface-subtle p-4 sm:col-span-1" data-id="element-1058">
                              <dt className="text-sm uppercase tracking-wide text-text-secondary" data-id="element-1059">
                                Gesamtgewicht
                              </dt>
                              <dd className="mt-1.5 font-heading text-2xl" data-id="element-1060">
                                {formatWeight(getWeightPerLot(scannedOrder) * scannedOrder.lots)}{' '}
                                kg
                              </dd>
                            </div>
                          </dl>

                          <section className="mt-8" aria-labelledby="running-materials-title" data-id="element-1061">
                            <h3 id="running-materials-title" className="font-heading text-xl text-text-primary" data-id="element-1062">
                              Materialien
                            </h3>
                            <div className="mt-4 overflow-hidden rounded-lg border border-border" data-id="element-1063">
                              <table className="w-full table-fixed text-left text-xs sm:text-sm" data-id="element-1064">
                                <colgroup data-id="element-1065">
                                  <col className="w-[20%]" data-id="element-1066" />
                                  <col className="w-[34%]" data-id="element-1067" />
                                  <col className="w-[14%]" data-id="element-1068" />
                                  <col className="w-[16%]" data-id="element-1069" />
                                  <col className="w-[16%]" data-id="element-1070" />
                                </colgroup>
                                <thead className="bg-surface-subtle uppercase tracking-wide text-text-secondary" data-id="element-1071">
                                  <tr data-id="element-1072">
                                    <th scope="col" className="px-2 py-3 font-normal sm:px-4" data-id="element-1073">
                                      Artikelnummer
                                    </th>
                                    <th scope="col" className="px-2 py-3 font-normal sm:px-4" data-id="element-1074">
                                      Beschreibung
                                    </th>
                                    <th scope="col" className="px-2 py-3 text-center font-normal sm:px-4" data-id="element-1075">
                                      Verwogen
                                    </th>
                                    <th scope="col" className="px-2 py-3 text-right font-normal sm:px-4" data-id="element-1076">
                                      Pro Lot
                                    </th>
                                    <th scope="col" className="px-2 py-3 text-right font-normal sm:px-4" data-id="element-1077">
                                      Gesamt
                                    </th>
                                  </tr>
                                </thead>
                                <tbody data-id="element-1078">
                                  {scannedOrder.materials.map(material => <tr key={material.articleNumber} className="border-t border-border" data-id="element-1079">
                                      <td className="break-words px-2 py-3 font-heading sm:px-4" data-id="element-1080">
                                        {material.articleNumber}
                                      </td>
                                      <td className="break-words px-2 py-3 text-text-secondary sm:px-4" data-id="element-1081">
                                        {material.description}
                                      </td>
                                      <td className="px-2 py-3 text-center sm:px-4" data-id="element-1082">
                                        {material.isWeighed ? <span className="inline-flex items-center justify-center text-success" aria-label="Verwogen" data-id="element-1083">
                                            <CheckIcon size={20} strokeWidth={2.6} aria-hidden="true" data-id="element-1084" />
                                            <span className="sr-only" data-id="element-1085">
                                              Verwogen
                                            </span>
                                          </span> : <span className="font-heading text-base text-text-secondary" aria-label="Noch nicht verwogen" data-id="element-1086">
                                            <span aria-hidden="true" data-id="element-1087">−</span>
                                            <span className="sr-only" data-id="element-1088">
                                              Noch nicht verwogen
                                            </span>
                                          </span>}
                                      </td>
                                      <td className="px-2 py-3 text-right font-heading sm:px-4" data-id="element-1089">
                                        {formatWeight(material.quantityPerLotKg)}{' '}
                                        kg
                                      </td>
                                      <td className="px-2 py-3 text-right font-heading sm:px-4" data-id="element-1090">
                                        {formatWeight(material.quantityPerLotKg * scannedOrder.lots)}{' '}
                                        kg
                                      </td>
                                    </tr>)}
                                </tbody>
                              </table>
                            </div>
                          </section>
                        </div>

                        <div className="flex flex-col-reverse gap-3 border-t border-border p-5 sm:flex-row sm:justify-end sm:p-6" data-id="element-1091">
                          <button type="button" onClick={closeScannerDialog} className="min-h-[56px] rounded-md border border-border bg-surface px-6 font-heading text-base text-text-primary transition-colors hover:bg-surface-subtle focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green" data-id="element-1092">
                            Abbrechen
                          </button>
                          <button type="button" onClick={startScannedOrder} className="min-h-[64px] rounded-md bg-brand-green px-10 font-heading text-lg text-text-primary transition-colors hover:bg-brand-green-dark focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green sm:min-w-[250px]" data-id="element-1093">
                            Auftrag fortsetzen
                          </button>
                        </div>
                      </> : <div className="p-6 text-center sm:p-10" data-id="element-1094">
                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-surface-subtle" data-id="element-1095">
                          <CircleAlertIcon className="text-text-secondary" size={32} strokeWidth={1.7} aria-hidden="true" data-id="element-1096" />
                        </div>
                        <h2 id="running-scanner-dialog-title" className="mt-6 font-heading text-3xl leading-tight sm:text-4xl" data-id="element-1097">
                          Auftrag nicht gefunden
                        </h2>
                        <p id="running-scanner-dialog-description" className="mx-auto mt-3 max-w-md text-lg leading-7 text-text-secondary" data-id="element-1098">
                          Für diesen Scan konnte kein laufender Auftrag
                          zugeordnet werden. Bitte Etikett prüfen und erneut
                          scannen.
                        </p>
                        <button type="button" onClick={closeScannerDialog} className="mt-8 min-h-[56px] rounded-md border border-border bg-surface px-7 font-heading text-base text-text-primary transition-colors hover:bg-surface-subtle focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green" data-id="element-1099">
                          Schließen
                        </button>
                      </div>}
                  </motion.section>
                </motion.div>}
            </AnimatePresence>
          </motion.div>
        </section>}
    </main>;
}
function RunningOrdersSkeleton() {
  return <>
      {[0, 1, 2, 3, 4].map(item => <li key={item} className="min-h-[148px] animate-pulse rounded-lg border border-border bg-surface p-3 shadow-sm sm:p-4" aria-hidden="true" data-id="element-1100">
          <div className="flex min-h-[116px] items-center gap-3 sm:gap-4" data-id="element-1101">
            <div className="h-12 w-12 shrink-0 rounded-md bg-border" data-id="element-1102" />
            <div className="min-w-0 flex-1" data-id="element-1103">
              <div className="h-5 w-44 max-w-full rounded bg-border sm:w-56" data-id="element-1104" />
              <div className="mt-3 h-4 w-32 rounded bg-border sm:w-48" data-id="element-1105" />
              <div className="mt-4 h-4 w-64 max-w-[80%] rounded bg-border" data-id="element-1106" />
              <div className="mt-4 h-2 w-28 rounded-full bg-border sm:w-40" data-id="element-1107" />
            </div>
            <div className="h-14 w-14 shrink-0 rounded-md bg-border sm:h-16 sm:w-16" data-id="element-1108" />
            <div className="h-14 w-14 shrink-0 rounded-md bg-border sm:h-16 sm:w-16" data-id="element-1109" />
          </div>
        </li>)}
    </>;
}
function getWeightPerLot(order: RunningOrder) {
  return order.materials.reduce((total, material) => total + material.quantityPerLotKg, 0);
}
function formatWeight(weightKg: number) {
  return weightKg.toLocaleString('de-DE');
}